import React, { useEffect, useState } from 'react'
import { getGpaPercentAverages, getOverviewTexts, saveOverviewTexts } from '../../../lib/studentcourselessonreport'
import { ReportCard, ToastNotification, OverviewTextarea, UpdateButton } from './CourseReportComponents'
import { getHeaderClass, sortLevels } from './CourseReportUtils'
import style from './CourseReportStyles.module.css'

export const CourseLessonAverageGpaReport = ({ studentId, isStudent = false }) => {
	const [data, setData] = useState({})
	const [overview, setOverview] = useState({})
	const [toastMessage, setToastMessage] = useState('')
	const [showToast, setShowToast] = useState(false)
	const [allLevels, setAllLevels] = useState([])
	const [allDepartments, setAllDepartments] = useState([])

	useEffect(() => {
		loadData()
	}, [studentId])

	const loadData = async () => {
		const gpaData = await getGpaPercentAverages(studentId)
		const overviewRes = await getOverviewTexts(studentId)

		const dataMap = {}
		const levelSet = new Set()
		const departmentSet = new Set()

		gpaData.forEach((item) => {
			const { department, studentId, ...rest } = item
			if (!dataMap[department]) dataMap[department] = {}

			Object.entries(rest).forEach(([key, value]) => {
				const level = key
				dataMap[department][level] = value ?? 0
				levelSet.add(level)
			})

			departmentSet.add(department)
		})

		const overviewMap = {}
		overviewRes.forEach((item) => {
			overviewMap[item.type] = item.desc
		})

		setData(dataMap)
		setOverview(overviewMap)
		setAllLevels(['Overall', ...sortLevels([...levelSet].filter((l) => l !== 'Overall'))])
		setAllDepartments([...departmentSet])
	}

	const handleOverviewChange = (value) => {
		setOverview((prev) => ({ ...prev, caverageTest: value }))
	}

	const handleUpdate = async () => {
		await saveOverviewTexts(studentId, { caverageTest: overview.caverageTest || '' })
		setShowToast(true)
		setToastMessage('Update successful!')
		setTimeout(() => setShowToast(false), 3000)
		loadData()
	}

	const getAvg = (value) => (value != null ? parseFloat(value).toFixed(1) : '')

	return (
		<ReportCard title="GPA">
			<ToastNotification show={showToast} message={toastMessage} onClose={() => setShowToast(false)} />

			<table className="table table-bordered text-center">
				<thead>
					<tr>
						<th style={{ width: '150px' }}>Department</th>
						{allLevels.map((lvl) => (
							<th key={lvl} className={style[getHeaderClass(lvl)]}>
								{lvl}
							</th>
						))}
						<th style={{ width: '400px' }}>Overview</th>
					</tr>
				</thead>
				<tbody>
					{allDepartments.map((dept, index) => (
						<tr key={dept}>
							<td>{dept}</td>
							{allLevels.map((lvl) => (
								<td key={lvl} className={data[dept]?.[lvl] > 0 ? style[getHeaderClass(lvl)] : ''}>
									{getAvg(data[dept]?.[lvl])}
								</td>
							))}

							{index === 0 && (
								<td rowSpan={allDepartments.length} className={style.overviewCell}>
									<div className="h-100 w-100 d-flex flex-column justify-content-between">
										<OverviewTextarea
											value={overview['caverageTest'] || ''}
											onChange={handleOverviewChange}
											rows={allDepartments.length * 2}
											isStudent={isStudent}
										/>
									</div>
								</td>
							)}
						</tr>
					))}
					{/* Average row */}
					<tr style={{ fontWeight: 'bold', background: '#f0f0f0' }}>
						<td>Average</td>
						{allLevels.map((lvl) => {
							let avg = 0
							if (lvl === 'Overall') {
								// Collect all values except 'Overall' columns, only > 0
								const values = []
								allDepartments.forEach((dept) => {
									Object.entries(data[dept] || {}).forEach(([key, value]) => {
										if (key !== 'Overall' && parseFloat(value) > 0) {
											values.push(parseFloat(value))
										}
									})
								})
								avg = values.length > 0 ? (values.reduce((sum, val) => sum + val, 0) / values.length).toFixed(1) : ''
							} else {
								const values = allDepartments.map((dept) => parseFloat(data[dept]?.[lvl]) || 0).filter((val) => val > 0)
								avg = values.length > 0 ? (values.reduce((sum, val) => sum + val, 0) / values.length).toFixed(1) : ''
							}
							return (
								<td key={lvl} className={[avg > 0 ? style[getHeaderClass(lvl)] : '', 'fw-bold'].filter(Boolean).join(' ')}>
									{avg}
								</td>
							)
						})}
						<td></td>
					</tr>
				</tbody>
			</table>

			<UpdateButton onClick={handleUpdate} isStudent={isStudent} />
		</ReportCard>
	)
}
